import { useAppSelector } from 'store/hooks';
import { selectUserPreference } from 'userPreferences/userPreferencesSlice';
import { useCallback, useEffect } from 'react';
import { useServices } from 'app/hooks';
import { NotificationType } from '@instinet-analytics/semantic-ui-components';
import { useDispatchShowNotification } from 'notification/store/dispatcher';

export const useUserPreference = <T>(key: string, defaultValue?: T) =>
    useAppSelector((s) => {
        return selectUserPreference<T>(s, key) ?? defaultValue;
    });

export function useFetchUserPreference<T = void>(
    key: string,
    initialValue?: T
) {
    const { userPreferencesService } = useServices();

    useEffect(() => {
        userPreferencesService.fetchUserPreference(key, initialValue);
    }, [key, userPreferencesService, initialValue]);
}

export function useUpdateUserPreference<T>(
    key: string
): (nextValue: T) => void {
    const dispatchNotification = useDispatchShowNotification();
    const { userPreferencesService } = useServices();

    return useCallback(
        (nextValue: T) => {
            userPreferencesService
                .saveUserPreference(key, nextValue)
                .then(() => {
                    dispatchNotification(
                        'User preference saved',
                        NotificationType.Info
                    );
                })
                .catch(() => {
                    dispatchNotification(
                        'Error saving user preference',
                        NotificationType.Warning
                    );
                });
        },
        [dispatchNotification, key, userPreferencesService]
    );
}
